import { useCallback, useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { fetchJson } from '@/lib/http'
import type { KnowledgeGraph } from '@/types/knowledge_graph'

interface Options {
  url: string
  kg: KnowledgeGraph
  initialLockVersion: number
  debounceMs?: number
}

interface AutosaveResult {
  isDirty: boolean
  showSaved: boolean
  saveConflict: boolean
  saveFailed: boolean
  triggerSave: () => void
}

export function useKnowledgeGraphAutosave ({ url, kg, initialLockVersion, debounceMs = 1500 }: Options): AutosaveResult {
  const [isDirty, setIsDirty] = useState(false)
  const [showSaved, setShowSaved] = useState(false)
  const [saveConflict, setSaveConflict] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)

  const kgRef = useRef(kg)
  kgRef.current = kg

  const kgLockVersionRef = useRef(initialLockVersion)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const saveDraft = useCallback(() => {
    fetchJson(url, {
      method: 'PUT',
      body: {
        knowledge_graph: kgRef.current,
        kg_lock_version: kgLockVersionRef.current
      }
    }).then(async (res) => {
      if (res.status === 409) {
        setSaveConflict(true)
        return
      }
      if (!res.ok) {
        setSaveFailed(true)
        return
      }
      const data = await res.json()
      kgLockVersionRef.current = data.kg_lock_version
      setIsDirty(false)
      setSaveFailed(false)
      setSaveConflict(false)
      clearTimeout(savedTimerRef.current)
      setShowSaved(true)
      savedTimerRef.current = setTimeout(() => setShowSaved(false), 2000)
    }).catch(() => {
      setSaveFailed(true)
    })
  }, [url])

  const triggerSave = useCallback(() => {
    clearTimeout(saveTimerRef.current)
    setIsDirty(true)
    setSaveFailed(false)
    setSaveConflict(false)
    saveTimerRef.current = setTimeout(saveDraft, debounceMs)
  }, [saveDraft, debounceMs])

  useEffect(() => {
    if (!isDirty) return
    function handleBeforeUnload (e: BeforeUnloadEvent): void {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  useEffect(() => {
    if (!isDirty) return
    return router.on('before', (event) => {
      if (!confirm('編集中の内容がまだ保存されていません。このまま移動しますか？')) {
        event.preventDefault()
      }
    })
  }, [isDirty])

  return { isDirty, showSaved, saveConflict, saveFailed, triggerSave }
}
